import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { Event } from '../../types';
import EventModal from '../Modals/EventModal';
import EventDetailModal from '../Modals/EventDetailModal';
import EventCard from '../Events/EventCard';
import CalendarView from '../Events/CalendarView';
import { 
  getSchoolEvents, 
  getCompanyEvents, 
  getTeacherEvents,
  getUserEvents,
  deleteSchoolEvent,
  deleteCompanyEvent,
  deleteTeacherEvent,
  deleteUserEvent,
  EventResponse
} from '../../api/Events';
import { getOrganizationId } from '../../utils/projectMapper';
import './Events.css';

const Events: React.FC = () => {
  const { state, addEvent, updateEvent, deleteEvent, setCurrentPage } = useAppContext();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [isEventsOverlayOpen, setIsEventsOverlayOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [periodFilter, setPeriodFilter] = useState<string>('all');
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open create event modal from URL (e.g. from Sidebar "Actions rapides" -> Programmer un événement)
  useEffect(() => {
    if (searchParams.get('open') !== 'create') return;
    setSelectedEvent(null);
    setIsDuplicateMode(false);
    setIsEventModalOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('open');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Transform API response to frontend Event format
  const transformEventResponse = (apiEvent: EventResponse): Event => {
    // Transform participants from API format to frontend format
    // Ensure all participants are included, regardless of their role
    const transformedParticipants = apiEvent.participants?.map(p => ({
      id: p.id.toString(),
      email: p.email,
      first_name: p.first_name,
      last_name: p.last_name,
      claim_token: p.claim_token || undefined,
      has_event_badges: p.has_event_badges || false,
      received_badge_ids: p.received_badge_ids || []
    })) || [];
    
    return {
      id: apiEvent.id.toString(),
      title: apiEvent.title,
      description: apiEvent.description || '',
      date: apiEvent.date,
      time: apiEvent.time,
      duration: apiEvent.duration,
      type: apiEvent.type as Event['type'],
      location: apiEvent.location || '',
      participants: transformedParticipants,
      groupIds: apiEvent.group_ids?.map((gid) => gid.toString()) || [],
      manualParticipantIds: apiEvent.manual_participant_ids || [],
      badges: apiEvent.badges?.map(b => b.toString()) || [],
      badgeSkills: apiEvent.badge_skills || {},
      image: apiEvent.image || '',
      status: apiEvent.status as Event['status'],
      projectId: '',
      createdBy: apiEvent.createdBy ?? undefined,
      createdAt: apiEvent.created_at,
      documents: apiEvent.documents || [],
    };
  };

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoadingEvents(true);
      setEventsError(null);

      try {
        const organizationId = getOrganizationId(state.user, state.showingPageType);
        let eventsResponse;

        if (state.showingPageType === 'edu' && organizationId) {
          eventsResponse = await getSchoolEvents(organizationId);
        } else if (state.showingPageType === 'pro' && organizationId) {
          eventsResponse = await getCompanyEvents(organizationId);
        } else if (state.showingPageType === 'teacher') {
          eventsResponse = await getTeacherEvents();
        } else if (state.showingPageType === 'user') {
          eventsResponse = await getUserEvents();
        } else {
          // Fallback to context events
          setEvents(state.events);
          setIsLoadingEvents(false);
          return;
        }

        // Transform API events to frontend format
        const transformedEvents = eventsResponse.data.map(transformEventResponse);
        setEvents(transformedEvents);
      } catch (error: any) {
        console.error('Error fetching events:', error);
        setEventsError(error.response?.data?.message || error.message || 'Erreur lors du chargement des événements');
        // Fallback to context events on error
        setEvents(state.events);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType, state.user.id]);

  const filteredEvents = events.filter(event => {
    // Search filter
    const matchesSearch = (event?.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (event?.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (event?.location?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    // Event type filter
    const matchesType = eventTypeFilter === 'all' || event.type === eventTypeFilter;
    
    // Period filter
    let matchesPeriod = true;
    if (periodFilter !== 'all') {
      const eventDate = new Date(event.date);
      const today = new Date();
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      
      switch (periodFilter) {
        case 'today':
          matchesPeriod = eventDate >= startOfToday && eventDate < endOfToday;
          break;
        case 'week':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 7);
          matchesPeriod = eventDate >= startOfWeek && eventDate < endOfWeek;
          break;
        case 'month':
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          matchesPeriod = eventDate >= startOfMonth && eventDate < endOfMonth;
          break;
        case 'quarter':
          const quarter = Math.floor(today.getMonth() / 3);
          const startOfQuarter = new Date(today.getFullYear(), quarter * 3, 1);
          const endOfQuarter = new Date(today.getFullYear(), quarter * 3 + 3, 1);
          matchesPeriod = eventDate >= startOfQuarter && eventDate < endOfQuarter;
          break;
      }
    }
    
    return matchesSearch && matchesType && matchesPeriod;
  });

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setIsEventDetailModalOpen(true);
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setIsDuplicateMode(false);
    setIsEventModalOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsDuplicateMode(false);
    setIsEventDetailModalOpen(false);
    setIsEventModalOpen(true);
  };

  const handleSaveEvent = async (eventData: Omit<Event, 'id'>) => {
    try {
      if (isDuplicateMode || !selectedEvent) {
        // Create new event (duplicate or fresh)
        const newEvent: Event = {
          ...eventData,
          id: Date.now().toString()
        };
        addEvent(newEvent);
        setSuccessMessage(isDuplicateMode ? 'Événement dupliqué avec succès !' : 'Événement créé avec succès !');
        // Add to local events
        setEvents(prev => [...prev, newEvent]);
        setIsDuplicateMode(false);
      }
      else {
        updateEvent(selectedEvent.id, eventData);
        setSuccessMessage('Événement modifié avec succès !');
        // Update local events
        setEvents(prev => prev.map(e => e.id === selectedEvent.id ? { ...e, ...eventData } : e));
      }
      setIsEventModalOpen(false);
      setSelectedEvent(null);
      setIsDuplicateMode(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

      // Reload events from API after a short delay to get the latest data
      setTimeout(async () => {
        const organizationId = getOrganizationId(state.user, state.showingPageType);
        try {
          let eventsResponse;
          if (state.showingPageType === 'edu' && organizationId) {
            eventsResponse = await getSchoolEvents(organizationId);
          } else if (state.showingPageType === 'pro' && organizationId) {
            eventsResponse = await getCompanyEvents(organizationId);
          } else if (state.showingPageType === 'teacher') {
            eventsResponse = await getTeacherEvents();
          } else if (state.showingPageType === 'user') {
            eventsResponse = await getUserEvents();
          } else {
            return;
          }
          const transformedEvents = eventsResponse.data.map(transformEventResponse);
          setEvents(transformedEvents);
        } catch (error) {
          console.error('Error reloading events:', error);
        }
      }, 1000);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDuplicateEvent = (eventToDuplicate: Event) => {
    // Open EventModal prefilled, but treat as creation
    const participantIds = (eventToDuplicate.participants || []).map((p) =>
      typeof p === 'object' ? p.id.toString() : p.toString()
    );

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const clonedEvent: Event = {
      ...eventToDuplicate,
      id: eventToDuplicate.id,
      title: `${eventToDuplicate.title} (copie)`,
      participants: participantIds,
      date: todayStr // reset date to today for the duplicate
    };

    setSelectedEvent(clonedEvent);
    setIsDuplicateMode(true);
    setIsEventDetailModalOpen(false);
    setIsEventModalOpen(true);
  };

  const handleDeleteEvent = async (id: string) => {
    // Confirmation dialog
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      return;
    }

    try {
      const organizationId = getOrganizationId(state.user, state.showingPageType);
      const eventId = parseInt(id);

      if (isNaN(eventId)) {
        console.error('Invalid event ID:', id);
        return;
      }

      // Call appropriate API based on context
      if (state.showingPageType === 'edu' && organizationId) {
        await deleteSchoolEvent(organizationId, eventId);
      } else if (state.showingPageType === 'pro' && organizationId) {
        await deleteCompanyEvent(organizationId, eventId);
      } else if (state.showingPageType === 'teacher') {
        await deleteTeacherEvent(eventId);
      } else if (state.showingPageType === 'user') {
        await deleteUserEvent(eventId);
      } else {
        // Fallback to context delete
        deleteEvent(id);
      }

      // Update local state
      setEvents(prev => prev.filter(e => e.id !== id));
      setSelectedEvent(null);
      setIsEventDetailModalOpen(false);
      setIsEventModalOpen(false); // Also close edit modal if open
      setSuccessMessage('Événement supprimé avec succès !');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      console.error('Error deleting event:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erreur lors de la suppression de l\'événement';
      alert(errorMessage);
    }
  };

  // const handleCopyLink = () => {
  //   navigator.clipboard.writeText(window.location.href);
  //   // TODO: Show notification
  // };

  return (
    <section className="events-container with-sidebar">
      {state.showingPageType === 'user' && (
        <div className="dashboard-back-link-wrap">
          <button type="button" className="dashboard-back-link" onClick={() => { setCurrentPage('dashboard'); navigate('/dashboard'); }}>
            ← Vers mon tableau de bord
          </button>
        </div>
      )}
      {/* Section Title + Actions */}
      <div className="section-title-row">
        <div className="section-title-left">
          <img src="/icons_logo/Icon=Event.svg" alt="Événements" className="section-icon" />
          <h2>Gestion des événements</h2>
        </div>
        <div className="events-actions">
          { state.showingPageType !== 'user' && (
            <button className="btn btn-primary" onClick={handleCreateEvent}>
              <i className="fas fa-plus"></i> Créer un événement
            </button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          <div className="success-content">
            <i className="fas fa-check-circle"></i>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="events-search-container">
        <div className="w-full search-bar">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher un événement par nom, date, lieu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filters-container">
          <div className="filter-group">
            <select 
              className="filter-select"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
            >
              <option value="all">Tous les types</option>
              <option value="session">Session</option>
              <option value="meeting">Réunion</option>
              <option value="workshop">Atelier</option>
              <option value="training">Formation</option>
              <option value="other">Autre</option>
            </select>
          </div>
          
          <div className="filter-group">
            <select 
              className="filter-select"
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
            >
              <option value="all">Toutes les périodes</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="quarter">Ce trimestre</option>
            </select>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="view-toggle">
        <button 
          className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
          onClick={() => setViewMode('calendar')}
        >
          <i className="fas fa-calendar"></i> Calendrier
        </button>
        <button 
          className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          <i className="fas fa-list"></i> Liste
        </button>
      </div>

      {/* Loading State */}
      {isLoadingEvents && (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin"></i> Chargement des événements...
        </div>
      )}

      {/* Error State */}
      {eventsError && !isLoadingEvents && (
        <div style={{ padding: '20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', margin: '10px 0' }}>
          <i className="fas fa-exclamation-circle"></i> {eventsError}
        </div>
      )}

      {/* Events Content */}
      {!isLoadingEvents && (
        <div className="min-h-screen events-layout">
          {viewMode === 'calendar' ? (
            <CalendarView 
              events={filteredEvents}
              currentDate={currentDate}
              onDateChange={setCurrentDate}
              onEventClick={handleEventClick}
              onCreateEvent={handleCreateEvent}
              onDuplicateEvent={(event) => handleDuplicateEvent(event)}
            />
          ) : (
            <div className="events-list">
              {filteredEvents.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  <i className="fas fa-calendar-times" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}></i>
                  <p>Aucun événement trouvé</p>
                </div>
              ) : (
                filteredEvents.map((event) => {
                  const currentUserId = state.user?.id == null ? '' : String(state.user.id);
                  const creatorId = typeof event.createdBy === 'object' && event.createdBy !== null && 'id' in event.createdBy
                    ? event.createdBy.id
                    : (event.createdBy ?? '');
                  const isEventCreator = Boolean(currentUserId && creatorId && currentUserId === creatorId);
                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      members={state.members}
                      onClick={() => handleEventClick(event)}
                      onEdit={isEventCreator ? () => handleEditEvent(event) : undefined}
                      onDelete={isEventCreator ? () => handleDeleteEvent(event.id) : undefined}
                      onDuplicate={() => handleDuplicateEvent(event)}
                    />
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Events Overlay */}
      {isEventsOverlayOpen && (
        <div className="events-overlay">
          <div className="events-overlay-header">
            <h3>Événements à venir</h3>
            <button className="btn-icon close-btn" onClick={() => setIsEventsOverlayOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="events-overlay-content">
            {filteredEvents.slice(0, 5).map((event) => {
              const currentUserId = state.user?.id == null ? '' : String(state.user.id);
              const creatorId = typeof event.createdBy === 'object' && event.createdBy !== null && 'id' in event.createdBy
                ? event.createdBy.id
                : (event.createdBy ?? '');
              const isEventCreator = Boolean(currentUserId && creatorId && currentUserId === creatorId);
              return (
              <div key={event.id} className="notification-card">
                <div className="notification-icon">
                  <img src="/icons_logo/Icon=Event.svg" alt="Event" />
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h3>{event.title}</h3>
                  </div>
                  <div className="notification-meta">
                    <div className="event-details-row">
                      <span className="event-meta-item">
                        <i className="fas fa-calendar-alt"></i> {event.date}
                      </span>
                      <span className="event-meta-item">
                        <i className="fas fa-clock"></i> {event.time}
                      </span>
                      <span className="event-meta-item">
                        <i className="fas fa-map-marker-alt"></i> {event.location}
                      </span>
                      <span className="event-meta-item">
                        <i className="fas fa-users"></i> {event.participants.length} participants
                      </span>
                    </div>
                  </div>
                </div>
                {isEventCreator && (
                <div className="notification-actions">
                  <button className="btn-icon" title="Modifier" onClick={() => handleEditEvent(event)}>
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="btn-icon" title="Supprimer" onClick={() => handleDeleteEvent(event.id)}>
                    <img src="/icons_logo/Icon=trash.svg" alt="Delete" className="action-icon" />
                  </button>
                </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {isEventDetailModalOpen && selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => {
            setIsEventDetailModalOpen(false);
            setSelectedEvent(null);
          }}
          onEdit={() => handleEditEvent(selectedEvent)}
          onDelete={() => {
            handleDeleteEvent(selectedEvent.id);
            setIsEventDetailModalOpen(false);
            setSelectedEvent(null);
          }}
          onComplete={async () => {
            // Refresh events after completion to get updated event data
            try {
              const organizationId = getOrganizationId(state.user, state.showingPageType);
              let eventsResponse;

              if (state.showingPageType === 'edu' && organizationId) {
                eventsResponse = await getSchoolEvents(organizationId);
              } else if (state.showingPageType === 'pro' && organizationId) {
                eventsResponse = await getCompanyEvents(organizationId);
              } else if (state.showingPageType === 'teacher') {
                eventsResponse = await getTeacherEvents();
              } else {
                return;
              }

              // Transform API events to frontend format
              const transformedEvents = eventsResponse.data.map(transformEventResponse);
              setEvents(transformedEvents);
              
              // Update selected event if it still exists
              const updatedEvent = transformedEvents.find(e => e.id === selectedEvent.id);
              if (updatedEvent) {
                setSelectedEvent(updatedEvent);
              }
            } catch (error) {
              console.error('Error refreshing events:', error);
            }
          }}
          onParticipantRemoved={async () => {
            // Refresh events to get updated participant list
            try {
              const organizationId = getOrganizationId(state.user, state.showingPageType);
              let eventsResponse;

              if (state.showingPageType === 'edu' && organizationId) {
                eventsResponse = await getSchoolEvents(organizationId);
              } else if (state.showingPageType === 'pro' && organizationId) {
                eventsResponse = await getCompanyEvents(organizationId);
              } else if (state.showingPageType === 'teacher') {
                eventsResponse = await getTeacherEvents();
              } else {
                return;
              }

              // Transform API events to frontend format
              const transformedEvents = eventsResponse.data.map(transformEventResponse);
              setEvents(transformedEvents);
              
              // Update selected event if it still exists
              const updatedEvent = transformedEvents.find(e => e.id === selectedEvent.id);
              if (updatedEvent) {
                setSelectedEvent(updatedEvent);
              }
            } catch (error) {
              console.error('Error refreshing events:', error);
            }
          }}
        />
      )}

      {/* Event Modal (for create/edit) */}
      {isEventModalOpen && (
        <EventModal
          event={selectedEvent}
          forceCreate={isDuplicateMode}
          onClose={() => {
            setIsEventModalOpen(false);
            setSelectedEvent(null);
            setIsDuplicateMode(false);
          }}
          onSave={handleSaveEvent}
        />
      )}
    </section>
  );
};

export default Events;
